import React, { useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { PerformanceStats, MonthlyReturn } from '../../types/marketplace';

interface PerformanceReportProps {
  title: string;
  icon: React.ReactNode;
  stats: PerformanceStats;
  period: string;
  accentColor?: 'emerald' | 'blue' | 'zebu';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const fmtInr = (n: number) =>
  '₹' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.abs(n));

const fmtInrSigned = (n: number) => (n >= 0 ? '+' : '-') + fmtInr(n);

/** Compact Indian notation for heatmap cells: Cr / L / K */
function fmtCompact(n: number): string {
  const sign = n >= 0 ? '+' : '-';
  const abs = Math.abs(n);
  if (abs >= 1_00_00_000) return `${sign}${(abs / 1_00_00_000).toFixed(1)}Cr`;
  if (abs >= 1_00_000)    return `${sign}${(abs / 1_00_000).toFixed(1)}L`;
  if (abs >= 1_000)       return `${sign}${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${abs}`;
}

/** Custom Recharts tooltip */
const ChartTooltip: React.FC<any> = ({ active, payload, label, valueLabel, valueFormatter }) => {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value;
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="text-gray-500 dark:text-gray-400 mb-1">{label}</p>
      <p className={`font-semibold ${val >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
        {valueLabel}: {valueFormatter ? valueFormatter(val) : val}
      </p>
    </div>
  );
};

/** Heatmap table showing Year × Month P&L */
const MonthlyHeatmap: React.FC<{ monthlyReturns: MonthlyReturn[] }> = ({ monthlyReturns }) => {
  const years = useMemo(() => {
    const map = new Map<number, Map<number, MonthlyReturn>>();
    for (const r of monthlyReturns) {
      if (!map.has(r.year)) map.set(r.year, new Map());
      map.get(r.year)!.set(r.month, r);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a - b);
  }, [monthlyReturns]);

  const maxAbsPnl = useMemo(
    () => Math.max(...monthlyReturns.map((r) => Math.abs(r.pnl)), 1),
    [monthlyReturns]
  );

  function cellStyle(pnl: number | undefined): React.CSSProperties {
    if (pnl === undefined) return {};
    const intensity = Math.min(Math.abs(pnl) / maxAbsPnl, 1);
    const opacity = 0.15 + intensity * 0.55;
    return pnl >= 0
      ? { backgroundColor: `rgba(16, 185, 129, ${opacity})` }  // emerald
      : { backgroundColor: `rgba(239, 68, 68, ${opacity})` };  // red
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse" style={{ fontSize: '0.75rem' }}>
        <thead>
          <tr>
            <th className="px-3 py-2 text-left font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-14">
              Year
            </th>
            {MONTHS.map((m) => (
              <th key={m} className="px-1 py-2 text-center font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" style={{ letterSpacing: '0.05em', minWidth: 64 }}>
                {m}
              </th>
            ))}
            <th className="px-2 py-2 text-center font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-l border-gray-200 dark:border-gray-700" style={{ minWidth: 80 }}>
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {years.map(([year, monthMap]) => {
            const yearTotal = Array.from(monthMap.values()).reduce((s, r) => s + r.pnl, 0);
            const yearPct = Array.from(monthMap.values()).reduce((s, r) => s + r.pctReturn, 0);
            return (
              <tr key={year} className="border-t border-gray-100 dark:border-gray-700/50">
                <td className="px-3 py-1 text-sm font-semibold text-gray-900 dark:text-white">{year}</td>
                {MONTHS.map((_, idx) => {
                  const r = monthMap.get(idx + 1);
                  return (
                    <td key={idx} className="px-1 py-1">
                      {r ? (
                        <div
                          className="h-14 rounded-lg flex flex-col items-center justify-center gap-0.5 transition-transform duration-200 hover:scale-[1.04] hover:shadow-md cursor-default"
                          style={{ ...cellStyle(r.pnl), fontVariantNumeric: 'tabular-nums' }}
                        >
                          <span className={`font-bold leading-none ${r.pnl >= 0 ? 'text-emerald-800 dark:text-emerald-100' : 'text-red-800 dark:text-red-100'}`} style={{ fontSize: '0.7rem' }}>
                            {fmtCompact(r.pnl)}
                          </span>
                          <span className={`leading-none opacity-80 ${r.pnl >= 0 ? 'text-emerald-700 dark:text-emerald-200' : 'text-red-700 dark:text-red-200'}`} style={{ fontSize: '0.55rem' }}>
                            {r.pctReturn >= 0 ? '+' : ''}{r.pctReturn}%
                          </span>
                        </div>
                      ) : (
                        <div className="h-14 rounded-lg flex items-center justify-center bg-gray-100/30 dark:bg-gray-700/30">
                          <span className="text-gray-300 dark:text-gray-600" style={{ fontSize: '0.6rem' }}>—</span>
                        </div>
                      )}
                    </td>
                  );
                })}
                <td className="px-1 py-1 border-l border-gray-200 dark:border-gray-700">
                  <div
                    className="h-14 rounded-lg flex flex-col items-center justify-center gap-0.5 bg-gray-50 dark:bg-gray-700/50 transition-transform duration-200 hover:scale-[1.04] cursor-default"
                    style={{ ...cellStyle(yearTotal), fontVariantNumeric: 'tabular-nums' }}
                  >
                    <span className={`font-bold leading-none ${yearTotal >= 0 ? 'text-emerald-800 dark:text-emerald-100' : 'text-red-800 dark:text-red-100'}`} style={{ fontSize: '0.7rem' }}>
                      {fmtCompact(yearTotal)}
                    </span>
                    <span className={`leading-none opacity-80 ${yearTotal >= 0 ? 'text-emerald-700 dark:text-emerald-200' : 'text-red-700 dark:text-red-200'}`} style={{ fontSize: '0.55rem' }}>
                      {yearPct >= 0 ? '+' : ''}{Math.round(yearPct * 10) / 10}%
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Legend */}
      <div className="flex items-center justify-end gap-4 mt-3">
        {[
          { color: 'rgba(16,185,129,0.55)', label: 'Profit' },
          { color: 'rgba(239,68,68,0.55)', label: 'Loss' },
          { color: 'rgba(156,163,175,0.2)', label: 'No data' },
        ].map(({ color, label }) => (
          <span key={label} className="flex items-center gap-1.5" style={{ fontSize: '0.6rem', color: '#9ca3af' }}>
            <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: color }} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

const ACCENT_PALETTE = {
  emerald: {
    iconCls: 'text-emerald-600 dark:text-emerald-400',
    badge: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    stroke: '#10b981',
    fill: '#10b981',
  },
  blue: {
    iconCls: 'text-blue-600 dark:text-blue-400',
    badge: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    stroke: '#3b82f6',
    fill: '#3b82f6',
  },
  zebu: {
    iconCls: 'text-zebu-600 dark:text-zebu-400',
    badge: 'bg-zebu-50 text-zebu-700 dark:bg-zebu-900/30 dark:text-zebu-300',
    stroke: '#0037B7',
    fill: '#0037B7',
  },
} as const;

const PerformanceReport: React.FC<PerformanceReportProps> = ({
  title,
  icon,
  stats,
  period,
  accentColor = 'emerald',
}) => {
  const accent = ACCENT_PALETTE[accentColor];

  // Thin out equity/drawdown data for performance (show up to 200 points)
  const equityData = useMemo(() => {
    const d = stats.dailyEquity;
    const step = Math.max(1, Math.floor(d.length / 200));
    return d.filter((_, i) => i % step === 0 || i === d.length - 1);
  }, [stats.dailyEquity]);

  const drawdownData = useMemo(() => {
    const d = stats.dailyDrawdown;
    const step = Math.max(1, Math.floor(d.length / 200));
    return d.filter((_, i) => i % step === 0 || i === d.length - 1);
  }, [stats.dailyDrawdown]);

  const axisStyle = { fontSize: 10, fill: '#9ca3af' };
  const gridStyle = 'rgba(156,163,175,0.15)';

  const statItems = [
    { label: 'Total Return', value: <span className={stats.totalReturn >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>{stats.totalReturn >= 0 ? '+' : ''}{stats.totalReturn}%</span> },
    { label: 'CAGR', value: <span className={stats.cagr >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>{stats.cagr >= 0 ? '+' : ''}{stats.cagr}% / yr</span> },
    { label: 'Net P&L', value: <span className={stats.netPnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>{fmtInrSigned(stats.netPnl)}</span> },
    { label: 'Win Rate', value: <span className="text-emerald-600 dark:text-emerald-400">{stats.winRate}%</span> },
    { label: 'Sharpe Ratio', value: stats.sharpeRatio },
    { label: 'Max Drawdown', value: <span className="text-red-600 dark:text-red-400">-{fmtInr(stats.maxDrawdown)}</span> },
    { label: 'Profit Factor', value: stats.profitFactor },
    { label: 'Total Trades', value: stats.totalTrades },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={accent.iconCls}>{icon}</span>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h2>
        </div>
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${accent.badge}`}>{period}</span>
      </div>

      {/* Key stats grid */}
      <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4 border-b border-gray-200 dark:border-gray-700">
        {statItems.map((item) => (
          <div key={item.label}>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{item.label}</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{item.value}</p>
          </div>
        ))}
      </div>

      {/* Equity Curve */}
      <div className="px-5 pt-5 pb-2 border-b border-gray-200 dark:border-gray-700">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
          Equity Curve
        </p>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={equityData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
            <defs>
              <linearGradient id={`equityGrad-${accentColor}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={accent.fill} stopOpacity={0.25} />
                <stop offset="95%" stopColor={accent.fill} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStyle} />
            <XAxis dataKey="date" tick={axisStyle} tickLine={false} interval="preserveStartEnd" />
            <YAxis
              tick={axisStyle}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `₹${Math.abs(v) >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`}
              width={52}
            />
            <ReferenceLine y={0} stroke="rgba(156,163,175,0.4)" strokeDasharray="4 2" />
            <Tooltip
              content={<ChartTooltip valueLabel="Equity" valueFormatter={fmtInrSigned} />}
              cursor={{ stroke: 'rgba(156,163,175,0.3)', strokeWidth: 1 }}
            />
            <Area
              type="monotone"
              dataKey="equity"
              stroke={accent.stroke}
              strokeWidth={2}
              fill={`url(#equityGrad-${accentColor})`}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Drawdown Curve */}
      <div className="px-5 pt-5 pb-2 border-b border-gray-200 dark:border-gray-700">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
          Drawdown
        </p>
        <ResponsiveContainer width="100%" height={160}>
          <AreaChart data={drawdownData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
            <defs>
              <linearGradient id="ddGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStyle} />
            <XAxis dataKey="date" tick={axisStyle} tickLine={false} interval="preserveStartEnd" />
            <YAxis
              tick={axisStyle}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `₹${Math.abs(v) >= 1000 ? (-Math.abs(v) / 1000).toFixed(0) + 'k' : v}`}
              width={52}
            />
            <ReferenceLine y={0} stroke="rgba(156,163,175,0.4)" strokeDasharray="4 2" />
            <Tooltip
              content={<ChartTooltip valueLabel="Drawdown" valueFormatter={(v: number) => fmtInrSigned(v)} />}
              cursor={{ stroke: 'rgba(156,163,175,0.3)', strokeWidth: 1 }}
            />
            <Area
              type="monotone"
              dataKey="drawdown"
              stroke="#ef4444"
              strokeWidth={2}
              fill="url(#ddGrad)"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Monthly P&L Heatmap */}
      <div className="px-5 py-4">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
          Monthly P&amp;L
        </p>
        <MonthlyHeatmap monthlyReturns={stats.monthlyReturns} />
      </div>
    </div>
  );
};

export default PerformanceReport;
